import React, {useContext} from 'react';
import {Grid, Typography} from '@mui/material';
import LocationFeatureList from './LocationFeatureList';
import ImageNotAvailablePlaceholder from '../ImageNotAvailablePlaceholder';
import InstantApprovalChip from './InstantApprovalChip';
import RequestSpaceChip from './RequestSpaceChip';
import AtCapacityChip from './AtCapacityChip';
import LocationAtCapacityNotice from './LocationAtCapacityNotice';
import LocationListItemImages from './LocationImages/LocationListItemImages';
import LocationItemPriceDisplay from '../LocationItemPriceDisplay';
import './style.css'
import LocationImageNav from './LocationImages/LocationImageNav';
import NotLiveLocationChip from "../../NotLiveLocationChip";
import LocationFavoriteIcon from '../LocationFavoriteIcon';
import {AppContext} from "../../../context/app-context";
import {isBuyerAccountType} from '../../../util/AccountUtils';
import {styled} from "@mui/material/styles";
import {useImageCarousel} from "../../../hooks/location/useImageCarousel";
import {getLocationGallery} from "../../../util/LocationMediaUtils";
import {useLocationTrustSignals} from "../../../hooks/location/useLocationTrustSignals";
import LocationTrustSignals from "../features/LocationTrustSignals";

// Location photos are stored at 4:3, so the result thumbnail shows them whole.
const THUMBNAIL_ASPECT_RATIO = '4 / 3';

const LocationListDistance = styled(Typography)(() => ({
  fontSize: '1.14rem',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500
}));

const LocationListItem = (props) => {
  const appContext = useContext(AppContext)
  const { location, showVisibilityChip, setListHover, isFavorite, isAtCapacity, showDistance = true } = props;
  const listingImage = location?.listingImageFileName;
  const gallery = getLocationGallery(location);
  const distance = location?.distance;

  // Total images = listing image (if exists) + gallery images
  const totalImages = (listingImage ? 1 : 0) + gallery.length;

  const { activeSlide, prevImage, nextImage } = useImageCarousel({ totalImages });
  const { trustSignals, otherFeatures } = useLocationTrustSignals(location?.features, location?.featureDetails);

  const { locationName } = location;
  let locationDescription = locationName ? locationName : `${location.city}, ${location.state}`;

  return (
    <div className='ss-listing-card' onMouseEnter={() => setListHover && setListHover(location)} onMouseLeave={() => setListHover && setListHover(null)}>
      <Grid item className='ss-listing-thumnail'>
        {
          listingImage ?
            <LocationListItemImages
              errorimg='/app-images/not-found-image/place_holder_image.png'
              placeholderimg="/app-images/loading/image-loader.gif"
              activeSlide={activeSlide}
              listingImageFileName={listingImage}
              gallery={gallery}
              locationId={location?.locationId}
              aspectratio={THUMBNAIL_ASPECT_RATIO}
            /> :
            <ImageNotAvailablePlaceholder aspectratio={THUMBNAIL_ASPECT_RATIO} />
        }
        <LocationImageNav activeSlide={activeSlide} totalImages={totalImages} onPrev={prevImage} onNext={nextImage} />
      </Grid>

      <Grid container direction={'column'} wrap={'nowrap'} className={"ss-profile-details"}>
        <Grid item>
          <Grid container direction={'row'} wrap='nowrap' justifyContent='space-between' alignItems='center'>
            <Grid item>
              <Grid container direction={'row'} spacing={2}>
                {
                  isAtCapacity ?
                    <Grid item><AtCapacityChip /></Grid> :
                    location?.hasInstantApproval ?
                      <Grid item><InstantApprovalChip /></Grid> :
                      <Grid item><RequestSpaceChip /></Grid>
                }
                {
                  showVisibilityChip && !location?.visible && <Grid item><NotLiveLocationChip /></Grid>
                }
              </Grid>
            </Grid>
            {showDistance && <Grid item><LocationListDistance>{distance} mi</LocationListDistance></Grid>}
          </Grid>
        </Grid>
        <Grid item>
          <Grid container direction={'row'} wrap='nowrap' justifyContent='space-between' alignItems='center'>
            <Grid item mt={-0.5}>
              <Typography className='ss-listing-title'>{locationDescription}</Typography>
            </Grid>
            {
              isBuyerAccountType(appContext?.user?.type) &&
              <Grid item>
                <LocationFavoriteIcon locationId={location.locationId} isFavorite={isFavorite} />
              </Grid>
            }
          </Grid>
        </Grid>
        {
          trustSignals.length > 0 &&
          // mb offsets the feature list's mt={-0.5} and adds its 0.7 row spacing, so the chips sit one row gap above it.
          <Grid item mt={-0.5} mb={1.2}>
            <LocationTrustSignals signals={trustSignals} />
          </Grid>
        }
        <Grid item className={'ss-spacer'} mt={-0.5}>
          <LocationFeatureList maxFeatures={4} features={otherFeatures} />
        </Grid>

        <Grid item>
          <LocationItemPriceDisplay
            pricePerMonth={location?.pricePerMonth ? location.pricePerMonth : 0}
            pricePerDay={location?.pricePerDay ? location.pricePerDay : 0}
          />
        </Grid>

        {
          isAtCapacity &&
          <Grid item>
            <LocationAtCapacityNotice nextAvailableDate={location?.nextAvailableDate} />
          </Grid>
        }
      </Grid>
    </div>
  )
}

export default LocationListItem;
